import { useState, useEffect } from "react";
import PathGame from "./PathGame";
import { Home, User, UserRound } from "lucide-react";

/**
 * Realtor Run — suburban sunset street.
 * Player: SVG blonde realtor (male or female, user-selectable) walking with briefcase.
 * Obstacle: FORECLOSED red stamp slamming down.
 * Lanes: colored houses with roofs, doors, windows, SALE→SOLD tag flip.
 * Overlay: sunset sky + distant house silhouettes + flying birds + pulsing sun.
 * Floor: sidewalk with cracks.
 */

const HOUSE_COLORS = [
  { wall: "#F4B183", roof: "#8B4513", door: "#5C2E0E" },
  { wall: "#B7C4CF", roof: "#4A5D6B", door: "#2E3B45" },
  { wall: "#E8C4A0", roof: "#7A4D2E", door: "#4A2C15" },
  { wall: "#C4D8B8", roof: "#4A6B3B", door: "#2D4225" },
  { wall: "#DDB4B4", roof: "#7A3B3B", door: "#4A2020" },
  { wall: "#D4C4A0", roof: "#8B6B3B", door: "#5C4520" },
];

const HAIR = "#F4D06F";
const HAIR_DARK = "#C99F3F";

function MaleRealtor(size = 34) {
  return (
    <svg width={size * 0.9} height={size} viewBox="0 0 34 44" style={{ filter: "drop-shadow(0 4px 6px rgba(0,0,0,0.5))" }} aria-hidden>
      <g style={{ animation: "realtorWalk 0.55s ease-in-out infinite" }}>
        {/* head */}
        <circle cx="17" cy="8" r="5" fill="#F5D0A9" />
        {/* short blonde hair — cropped, side-parted */}
        <path d="M12 8 Q11 3 17 2 Q23 3 22 8 L21 6 Q17 3 13 6 Z" fill={HAIR} />
        <path d="M17 4 Q20 3 21 5 L20 7 Q18 5 17 5 Z" fill={HAIR_DARK} opacity="0.9" />
        {/* eyes & smile */}
        <circle cx="15.5" cy="8" r="0.5" fill="#1E293B" />
        <circle cx="18.5" cy="8" r="0.5" fill="#1E293B" />
        <path d="M15.5 10 Q17 11 18.5 10" stroke="#7A3B2E" strokeWidth="0.5" fill="none" strokeLinecap="round" />
        {/* neck */}
        <rect x="15.5" y="12" width="3" height="2" fill="#E8B889" />
        {/* SUIT body — clear rectangular jacket, well-defined shoulders */}
        <path d="M8 14 L26 14 L28 32 L6 32 Z" fill="#1F2937" />
        {/* shoulders highlight */}
        <path d="M8 14 L10 17 L8 20 Z" fill="#0F172A" />
        <path d="M26 14 L24 17 L26 20 Z" fill="#0F172A" />
        {/* white shirt V + RED TIE (male signal) */}
        <path d="M13 14 L21 14 L20 24 L14 24 Z" fill="#F8FAFC" />
        <path d="M16.5 14 L17.5 14 L18.5 23 L15.5 23 Z" fill="#D4183D" />
        <path d="M16 22 L18 22 L18.5 25 L15.5 25 Z" fill="#B91240" />
        {/* arms */}
        <rect x="5" y="15" width="4" height="14" fill="#1F2937" rx="1.5" style={{ transformOrigin: "7px 17px", animation: "realtorArmL 0.55s ease-in-out infinite" }} />
        <rect x="25" y="15" width="4" height="14" fill="#1F2937" rx="1.5" style={{ transformOrigin: "27px 17px", animation: "realtorArmR 0.55s ease-in-out infinite" }} />
        {/* briefcase */}
        <g style={{ transformOrigin: "29px 26px", animation: "realtorCase 0.55s ease-in-out infinite" }}>
          <rect x="26" y="23" width="7" height="5" fill="#5C3B1E" stroke="#3B2814" strokeWidth="0.5" />
          <rect x="28.5" y="21.5" width="2" height="1.5" fill="#5C3B1E" />
          <rect x="28" y="25" width="3" height="0.6" fill="#B8863B" />
        </g>
        {/* dress-pant legs — straight & wide */}
        <rect x="11" y="32" width="4.5" height="10" fill="#0F172A" style={{ transformOrigin: "13px 33px", animation: "realtorLegL 0.55s ease-in-out infinite" }} />
        <rect x="18.5" y="32" width="4.5" height="10" fill="#0F172A" style={{ transformOrigin: "20px 33px", animation: "realtorLegR 0.55s ease-in-out infinite" }} />
        {/* dress shoes */}
        <ellipse cx="13.25" cy="43" rx="3" ry="1.2" fill="#000" />
        <ellipse cx="20.75" cy="43" rx="3" ry="1.2" fill="#000" />
      </g>
      <style>{`
        @keyframes realtorWalk { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-1.5px); } }
        @keyframes realtorArmL { 0%,100% { transform: rotate(-10deg); } 50% { transform: rotate(15deg); } }
        @keyframes realtorArmR { 0%,100% { transform: rotate(15deg); } 50% { transform: rotate(-10deg); } }
        @keyframes realtorLegL { 0%,100% { transform: rotate(-10deg); } 50% { transform: rotate(10deg); } }
        @keyframes realtorLegR { 0%,100% { transform: rotate(10deg); } 50% { transform: rotate(-10deg); } }
        @keyframes realtorCase { 0%,100% { transform: rotate(-4deg) translateY(0); } 50% { transform: rotate(4deg) translateY(-1px); } }
      `}</style>
    </svg>
  );
}

function FemaleRealtor(size = 34) {
  return (
    <svg width={size * 0.9} height={size} viewBox="0 0 34 44" style={{ filter: "drop-shadow(0 4px 6px rgba(0,0,0,0.5))" }} aria-hidden>
      <g style={{ animation: "realtorWalk 0.55s ease-in-out infinite" }}>
        {/* head */}
        <circle cx="17" cy="8" r="5" fill="#F5D0A9" />
        {/* LONG blonde hair — falls behind shoulders, distinctly feminine silhouette */}
        <path d="M11 8 Q10 18 12 26 L14 26 L14 10 Q13 4 17 3 Q21 4 20 10 L20 26 L23 26 Q25 18 24 8 Q23 2 17 2 Q11 2 11 8 Z" fill={HAIR} />
        {/* side-swept front bang */}
        <path d="M12 7 Q17 3 22 7 L21 9 Q17 5 14 9 Z" fill={HAIR_DARK} opacity="0.7" />
        {/* PONY TAIL falling behind body */}
        <path d="M11 24 Q7 30 8 38 Q10 34 11 30 Q12 34 12 40 Q13 34 13 28 Z" fill={HAIR} />
        <path d="M9 28 Q8 34 9 38" stroke={HAIR_DARK} strokeWidth="0.4" fill="none" opacity="0.6" />
        {/* earrings */}
        <circle cx="12.5" cy="9" r="0.5" fill="#E2B96A" />
        <circle cx="21.5" cy="9" r="0.5" fill="#E2B96A" />
        {/* eyes with lashes */}
        <path d="M14.5 7.2 L15 6.5 M15.5 7 L15.5 6.3 M18.5 7 L18.5 6.3 M19.5 7.2 L19 6.5" stroke="#1E293B" strokeWidth="0.35" strokeLinecap="round" />
        <ellipse cx="15.5" cy="8" rx="0.55" ry="0.7" fill="#1E293B" />
        <ellipse cx="18.5" cy="8" rx="0.55" ry="0.7" fill="#1E293B" />
        {/* blush */}
        <ellipse cx="13" cy="10" rx="1" ry="0.6" fill="#F4A5A5" opacity="0.7" />
        <ellipse cx="21" cy="10" rx="1" ry="0.6" fill="#F4A5A5" opacity="0.7" />
        {/* lips */}
        <path d="M15.8 10.3 Q17 11 18.2 10.3" stroke="#C13E5E" strokeWidth="0.7" fill="none" strokeLinecap="round" />
        {/* neck */}
        <rect x="15.5" y="12" width="3" height="2" fill="#E8B889" />
        {/* BLOUSE — softer color, no tie */}
        <path d="M8 14 L26 14 L27 28 L7 28 Z" fill="#7F1D3F" />
        <path d="M8 14 L10 17 L8 20 Z" fill="#5C0F2A" />
        <path d="M26 14 L24 17 L26 20 Z" fill="#5C0F2A" />
        {/* V-neck blouse detail with pearl necklace */}
        <path d="M14 14 L17 20 L20 14 Z" fill="#F8FAFC" />
        <path d="M14 15 Q17 17 20 15" stroke="#E2B96A" strokeWidth="0.5" fill="none" />
        <circle cx="17" cy="16.5" r="0.5" fill="#F8FAFC" stroke="#E2B96A" strokeWidth="0.2" />
        {/* arms */}
        <rect x="5" y="15" width="4" height="12" fill="#7F1D3F" rx="1.5" style={{ transformOrigin: "7px 17px", animation: "realtorArmL 0.55s ease-in-out infinite" }} />
        <rect x="25" y="15" width="4" height="12" fill="#7F1D3F" rx="1.5" style={{ transformOrigin: "27px 17px", animation: "realtorArmR 0.55s ease-in-out infinite" }} />
        {/* PENCIL SKIRT — clearly distinct from pants, narrows toward knee */}
        <path d="M7 28 L27 28 L25 38 L9 38 Z" fill="#1F1F2E" />
        <path d="M7 28 L27 28 L25 30 L9 30 Z" fill="#0F0F1B" opacity="0.5" />
        {/* folio / clutch */}
        <g style={{ transformOrigin: "29px 26px", animation: "realtorCase 0.55s ease-in-out infinite" }}>
          <rect x="26" y="22" width="7" height="5" fill="#B45309" stroke="#78350F" strokeWidth="0.4" rx="0.5" />
          <circle cx="29.5" cy="24.5" r="0.5" fill="#E2B96A" />
        </g>
        {/* bare legs */}
        <rect x="12" y="38" width="2.5" height="4" fill="#F5D0A9" style={{ transformOrigin: "13.25px 38px", animation: "realtorLegL 0.55s ease-in-out infinite" }} />
        <rect x="19.5" y="38" width="2.5" height="4" fill="#F5D0A9" style={{ transformOrigin: "20.75px 38px", animation: "realtorLegR 0.55s ease-in-out infinite" }} />
        {/* HIGH HEELS — clearly feminine */}
        <path d="M11 43 L15 43 L14.2 41 L13.5 43 L13 41 Z" fill="#5C0F2A" />
        <path d="M19 43 L23 43 L22 41 L21.5 43 L21 41 Z" fill="#5C0F2A" />
        <rect x="12.5" y="42" width="0.4" height="1.5" fill="#5C0F2A" />
        <rect x="21.1" y="42" width="0.4" height="1.5" fill="#5C0F2A" />
      </g>
    </svg>
  );
}

function ForeclosureStamp() {
  return (
    <svg width="46" height="44" viewBox="0 0 46 44" aria-hidden style={{ filter: "drop-shadow(0 6px 8px rgba(0,0,0,0.4))" }}>
      <g transform="rotate(-8 23 22)">
        <rect x="2" y="6" width="42" height="30" rx="2" fill="#0B0D10" stroke="#D4183D" strokeWidth="3" />
        <rect x="5" y="9" width="36" height="24" fill="none" stroke="#D4183D" strokeWidth="1.5" strokeDasharray="3 2" />
        <text x="23" y="20" textAnchor="middle" fontFamily="Impact, sans-serif" fontSize="9" fontWeight="900" fill="#D4183D" letterSpacing="0.5">FORE-</text>
        <text x="23" y="30" textAnchor="middle" fontFamily="Impact, sans-serif" fontSize="9" fontWeight="900" fill="#D4183D" letterSpacing="0.5">CLOSED</text>
      </g>
    </svg>
  );
}

function HouseLane(index, passed) {
  const c = HOUSE_COLORS[index % HOUSE_COLORS.length];
  const litDelay = (index * 0.31) % 3;
  return (
    <>
      {/* house body */}
      <div className="absolute inset-x-1 rounded-sm" style={{
        top: "42%", bottom: "18%",
        background: `linear-gradient(180deg, ${c.wall} 0%, ${c.wall}DD 100%)`,
        opacity: passed ? 0.55 : 1,
        boxShadow: "inset -3px 0 rgba(0,0,0,0.15), inset 0 -2px rgba(0,0,0,0.2)",
      }} />
      {/* roof triangle */}
      <div className="absolute left-1/2 -translate-x-1/2" style={{
        top: "30%",
        width: 0, height: 0,
        borderLeft: "20px solid transparent",
        borderRight: "20px solid transparent",
        borderBottom: `16px solid ${c.roof}`,
        opacity: passed ? 0.55 : 1,
        filter: "drop-shadow(0 2px 1px rgba(0,0,0,0.3))",
      }} />
      {/* chimney with smoke on unsold */}
      <div className="absolute" style={{ top: "28%", right: "22%", width: 4, height: 8, background: c.roof, opacity: passed ? 0.5 : 0.9 }} />
      {!passed && index % 2 === 0 && (
        <div className="absolute" style={{
          top: "20%", right: "23%",
          width: 5, height: 5, borderRadius: "50%",
          background: "rgba(200,200,200,0.4)",
          filter: "blur(2px)",
          animation: `chimneyPuff 3s ${(index * 0.5) % 3}s ease-out infinite`,
        }} />
      )}
      {/* door */}
      <div className="absolute left-1/2 -translate-x-1/2" style={{
        bottom: "18%",
        width: 9, height: "18%",
        background: c.door, borderRadius: "3px 3px 0 0",
        opacity: passed ? 0.55 : 1,
      }} />
      <div className="absolute left-1/2 -translate-x-1/2" style={{ bottom: "24%", marginLeft: 2, width: 1, height: 1, background: "#E2B96A" }} />
      {/* windows — lit ones flicker warmly on unsold houses */}
      <div className="absolute flex gap-1" style={{ top: "52%", left: "18%", right: "18%", justifyContent: "space-between" }}>
        <div style={{
          width: 7, height: 7, background: passed ? "#94A3B8" : "#FFF3B0",
          boxShadow: passed ? "inset 0 0 0 1px rgba(0,0,0,0.4)" : `inset 0 0 0 1px rgba(0,0,0,0.4), 0 0 6px rgba(255,220,140,0.5)`,
          opacity: passed ? 0.5 : 1,
          animation: passed ? undefined : `windowFlicker 4s ${litDelay}s ease-in-out infinite`,
        }} />
        <div style={{
          width: 7, height: 7, background: passed ? "#94A3B8" : "#FFF3B0",
          boxShadow: passed ? "inset 0 0 0 1px rgba(0,0,0,0.4)" : `inset 0 0 0 1px rgba(0,0,0,0.4), 0 0 6px rgba(255,220,140,0.5)`,
          opacity: passed ? 0.5 : 1,
          animation: passed ? undefined : `windowFlicker 4s ${litDelay + 1.3}s ease-in-out infinite`,
        }} />
      </div>
      {/* SOLD sign on passed houses */}
      {passed && (
        <div className="absolute left-1/2 -translate-x-1/2" style={{ top: "18%", animation: "soldPop 0.4s ease-out" }}>
          <div style={{
            background: "#DC2626", color: "#FFF", fontSize: 7, fontWeight: 900,
            padding: "1px 4px", borderRadius: 2, transform: "rotate(-10deg)",
            fontFamily: "Impact, sans-serif", letterSpacing: 0.5,
            boxShadow: "0 2px 3px rgba(0,0,0,0.4)",
          }}>SOLD</div>
        </div>
      )}
      {/* FOR SALE sign on unsold houses */}
      {!passed && (
        <div className="absolute left-1/2 -translate-x-1/2" style={{ bottom: "6%" }}>
          <div style={{ width: 1, height: 8, background: "#3B2814", margin: "0 auto" }} />
          <div style={{
            background: "#FFF8E7", color: "#B91C1C", fontSize: 5.5, fontWeight: 900,
            padding: "1px 3px", borderRadius: 1, fontFamily: "sans-serif", marginTop: -8,
            boxShadow: "0 1px 2px rgba(0,0,0,0.5)", letterSpacing: 0.2,
            border: "0.5px solid #B91C1C", whiteSpace: "nowrap", lineHeight: 1,
          }}>FOR SALE</div>
        </div>
      )}
    </>
  );
}

function SunsetSky() {
  return (
    <div className="absolute inset-x-0 top-0 pointer-events-none" style={{ height: "42%" }}>
      <div className="absolute inset-0" style={{
        background: "linear-gradient(180deg, #2A1B3D 0%, #7B4A5F 30%, #E67E5C 60%, #F4B183 85%, #F4CFA0 100%)",
      }} />
      {/* sun with pulsing glow */}
      <div className="absolute" style={{
        top: "30%", right: "12%",
        width: 36, height: 36, borderRadius: "50%",
        background: "radial-gradient(circle, #FFE9B0 0%, #F4A460 60%, transparent 100%)",
        boxShadow: "0 0 40px 10px rgba(244, 164, 96, 0.6)",
        animation: "sunPulse 4s ease-in-out infinite",
      }} />
      {/* distant house silhouettes — a full row on the horizon */}
      <svg className="absolute inset-x-0 bottom-0" width="100%" height="42" viewBox="0 0 400 42" preserveAspectRatio="none" aria-hidden>
        {/* far layer */}
        <path d="M0 42 L0 30 L15 22 L30 30 L45 22 L60 32 L80 24 L95 30 L110 22 L125 30 L145 22 L165 32 L180 24 L200 30 L220 22 L240 30 L260 22 L280 32 L295 24 L315 30 L335 22 L355 32 L380 26 L400 30 L400 42 Z" fill="#2A1E2E" opacity="0.8" />
        {/* nearer layer with roof caps */}
        <path d="M0 42 L0 34 L12 26 L24 34 L40 34 L52 26 L64 34 L80 34 L92 26 L104 34 L120 34 L134 24 L148 34 L164 34 L176 26 L188 34 L204 34 L218 24 L232 34 L248 34 L260 26 L272 34 L288 34 L302 24 L316 34 L332 34 L344 26 L356 34 L372 34 L384 26 L400 34 L400 42 Z" fill="#3B2A4A" opacity="0.85" />
        {/* tiny distant windows */}
        <g fill="#FFE9B0" opacity="0.5">
          <rect x="14" y="30" width="1" height="1.5" />
          <rect x="42" y="30" width="1" height="1.5" />
          <rect x="82" y="30" width="1" height="1.5" />
          <rect x="106" y="30" width="1" height="1.5" />
          <rect x="150" y="30" width="1" height="1.5" />
          <rect x="182" y="30" width="1" height="1.5" />
          <rect x="234" y="30" width="1" height="1.5" />
          <rect x="290" y="30" width="1" height="1.5" />
          <rect x="318" y="30" width="1" height="1.5" />
          <rect x="358" y="30" width="1" height="1.5" />
        </g>
      </svg>
      {/* birds flying across */}
      <svg className="absolute" style={{ top: "15%", left: 0, width: "100%", height: 12, animation: "birdsFly 22s linear infinite" }} viewBox="0 0 300 12" aria-hidden>
        <path d="M20 6 Q23 2 26 6 Q29 2 32 6" stroke="#1F1424" strokeWidth="1" fill="none" strokeLinecap="round" />
        <path d="M50 8 Q52 5 54 8 Q56 5 58 8" stroke="#1F1424" strokeWidth="0.8" fill="none" strokeLinecap="round" />
        <path d="M75 4 Q78 1 81 4 Q84 1 87 4" stroke="#1F1424" strokeWidth="1" fill="none" strokeLinecap="round" />
      </svg>
      {/* power line with pole shadow */}
      <div className="absolute inset-x-0" style={{ top: "88%", height: 1, background: "rgba(0,0,0,0.5)" }} />
    </div>
  );
}

function Sidewalk() {
  return (
    <div className="absolute inset-x-0 bottom-0 pointer-events-none" style={{
      height: "16%",
      background: "linear-gradient(180deg, #7A756A 0%, #4A4A3E 100%)",
      backgroundImage: "linear-gradient(180deg, #7A756A 0%, #4A4A3E 100%), repeating-linear-gradient(90deg, transparent 0 60px, rgba(0,0,0,0.25) 60px 62px)",
      boxShadow: "inset 0 2px 0 rgba(0,0,0,0.35)",
    }} />
  );
}

function GenderToggle({ value, onChange }) {
  return (
    <div>
      <div className="text-xs font-mono uppercase tracking-widest text-slate-500 mb-2">Agent</div>
      <div className="grid grid-cols-2 gap-2">
        <button
          type="button"
          onClick={() => onChange("male")}
          data-testid="realtor-gender-male"
          className={`inline-flex items-center justify-center gap-2 p-3 rounded-xl border transition text-sm ${value === "male" ? "border-[color:var(--gold)] text-[color:var(--gold)] bg-[rgba(226,185,106,0.06)]" : "border-white/10 text-slate-300 hover:border-white/20"}`}>
          <User className="w-4 h-4" /> Male · Blonde
        </button>
        <button
          type="button"
          onClick={() => onChange("female")}
          data-testid="realtor-gender-female"
          className={`inline-flex items-center justify-center gap-2 p-3 rounded-xl border transition text-sm ${value === "female" ? "border-[color:var(--gold)] text-[color:var(--gold)] bg-[rgba(226,185,106,0.06)]" : "border-white/10 text-slate-300 hover:border-white/20"}`}>
          <UserRound className="w-4 h-4" /> Female · Blonde
        </button>
      </div>
    </div>
  );
}

export default function Realtor() {
  const [gender, setGender] = useState(() => localStorage.getItem("realtor_gender") || "male");
  useEffect(() => { localStorage.setItem("realtor_gender", gender); }, [gender]);

  const playerSprite = (size) => (gender === "female" ? FemaleRealtor(size) : MaleRealtor(size));

  return (
    <PathGame
      slug="realtor"
      title="Realtor Run"
      headline="Sell · pocket · repeat"
      icon={<Home className="w-3.5 h-3.5" />}
      actionVerb="Sell house"
      laneWord="house"
      playerSprite={playerSprite}
      obstacleSprite={ForeclosureStamp}
      laneVariant={HouseLane}
      backgroundGradient="linear-gradient(180deg,#2A1B3D 0%,#7B4A5F 25%,#E67E5C 45%,#F4B183 60%,#8B7355 75%,#4A4A3E 100%)"
      sceneOverlay={<SunsetSky />}
      sceneFloor={<Sidewalk />}
      obstacleKeyframes={`
        @keyframes obstaclePass { 0% { transform: translate(-50%, -180%) rotate(-30deg); opacity: 0; } 55% { transform: translate(-50%, 90%) rotate(-8deg); opacity: 1; } 65% { transform: translate(-50%, 90%) rotate(-8deg) scale(1.15); } 100% { transform: translate(-50%, 90%) rotate(-8deg) scale(1); opacity: 1; } }
        @keyframes sunPulseMM { 0%,100% { box-shadow: 0 0 40px 10px rgba(244,164,96,0.6); } 50% { box-shadow: 0 0 55px 16px rgba(244,164,96,0.85); } }
        @keyframes sunPulse { 0%,100% { box-shadow: 0 0 40px 10px rgba(244,164,96,0.6); } 50% { box-shadow: 0 0 55px 16px rgba(244,164,96,0.85); } }
        @keyframes birdsFly { 0% { transform: translateX(-40%); } 100% { transform: translateX(120%); } }
        @keyframes chimneyPuff { 0% { transform: translateY(0) scale(1); opacity: 0.5; } 100% { transform: translateY(-14px) scale(2); opacity: 0; } }
        @keyframes windowFlicker { 0%,100% { filter: brightness(1); } 45% { filter: brightness(1.15); } 55% { filter: brightness(0.9); } }
        @keyframes soldPop { 0% { transform: scale(0.4) rotate(-30deg); opacity: 0; } 60% { transform: scale(1.2) rotate(-8deg); opacity: 1; } 100% { transform: scale(1) rotate(-10deg); opacity: 1; } }
      `}
      minHeight={280}
      preGameSlot={<GenderToggle value={gender} onChange={setGender} />}
      obstacleOnlyOnHit={true}
    />
  );
}
